import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaTimesCircle, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toastVariants } from '../utils/animationVariants';
import './../styles/Toast.css';

const Toast = ({ title, body, message, type, url, onDismiss }) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 5000); // Auto-dismiss after 5 seconds

    return () => clearTimeout(timer);
  }, [onDismiss]);

  const handleClick = () => {
    if (url) {
      // Ensure URL is relative for internal navigation if it's on the same domain
      let targetUrl = url;
      try {
        const urlObj = new URL(url, window.location.origin);
        if (urlObj.origin === window.location.origin) {
          targetUrl = urlObj.pathname + urlObj.search + urlObj.hash;
        }
      } catch (e) {
        // Not a full URL, keep as is
      }
      navigate(targetUrl);
      onDismiss();
    }
  };

  const typeMap = {
    success: {
      icon: <FaCheckCircle />,
      className: 'toast-success',
    },
    error: {
      icon: <FaTimesCircle />,
      className: 'toast-danger',
    },
    danger: {
      icon: <FaTimesCircle />,
      className: 'toast-danger',
    },
    warning: {
      icon: <FaExclamationTriangle />,
      className: 'toast-warning',
    },
    info: {
      icon: <FaInfoCircle />,
      className: 'toast-info',
    },
  };

  const { icon, className } = typeMap[type] || typeMap.info;

  const displayTitle = title || (type && type.charAt(0).toUpperCase() + type.slice(1)) || 'Notification';
  const displayBody = body || message;

  if (!displayBody && !displayTitle) return null;

  return (
    <motion.div
      className={`toast ${className} ${url ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
      variants={toastVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      layout
    >
      <motion.span
        className="toast-icon"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
      >
        {icon}
      </motion.span>
      <div className="toast-content">
        {displayTitle && <div className="toast-title font-bold text-sm leading-tight mb-0.5">{displayTitle}</div>}
        {displayBody && <div className="toast-message text-xs opacity-90">{displayBody}</div>}
      </div>
      <button 
        onClick={(e) => { e.stopPropagation(); onDismiss(); }} 
        className="toast-dismiss-button"
      >
        &times;
      </button>
      <motion.div
        className="toast-progress"
        initial={{ scaleX: 1 }}
        animate={{ scaleX: 0 }}
        transition={{ duration: 5, ease: 'linear' }}
      />
    </motion.div>
  );
};

export const ToastContainer = ({ toasts, onDismiss }) => {
  return (
    <div className="toast-container">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onDismiss={() => onDismiss(toast.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Toast;
